import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, IconButton, List, ListItem,
  Paper, Stack, Switch, TextField, Tooltip, Typography, Alert,
} from '@mui/material';
import ScenarioPickerDialog from '../../components/ScenarioPickerDialog/ScenarioPickerDialog';
import AddIcon from '@mui/icons-material/Add';
import ArticleIcon from '@mui/icons-material/Article';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import NumberInput from '../../components/NumberInput/NumberInput';
import ModalActionFooter from '../../components/ModalActionFooter/ModalActionFooter';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { toggleTheme, setSelectedTheme, THEME_MODE_KEY, THEME_ID_KEY } from '../../store/slices/themeSlice';
import { setPods } from '../../store/slices/podsSlice';
import { setEpics } from '../../store/slices/epicsSlice';
import { setCategories } from '../../store/slices/categoriesSlice';
import { setSprintSettings, DEFAULT_SPRINT_LENGTH, DEFAULT_TOTAL_SPRINTS, setWorkstreamSprints } from '../../store/slices/sprintsSlice';
import {
  setEpicWeights,
  setStoryWeights,
  HealthWeightMap,
} from '../../store/slices/healthConfigSlice';
import { EPIC_HEALTH_DEFINITIONS } from '../../store/slices/epicsSlice';
import { STORY_HEALTH_DEFINITIONS } from '../../store/slices/storiesSlice';
import {
  addEpicStatus, updateEpicStatus, removeEpicStatus, setEpicStatuses, moveEpicStatus,
  addStoryStatus, updateStoryStatus, removeStoryStatus, setStoryStatuses, moveStoryStatus,
  PARENT_STATES, PARENT_STATE_COLORS,
  ParentState, StatusEntry, createStatusEntry,
} from '../../store/slices/stateManagementSlice';
import { setWorkstreams } from '../../store/slices/workstreamsSlice';
import { validateEpicExportData, EpicExportData } from '../../utils/epicConfigValidation';
import { appThemes } from '../../themes';
import { categoryColors } from '../../theme';
import { toggleDeveloperMode } from '../../store/slices/developerModeSlice';

interface Section {
  id: string;
  label: string;
  subSections?: { id: string; label: string }[];
}

const sections: Section[] = [
  {
    id: 'display-settings',
    label: 'Display Settings',
    subSections: [
      { id: 'ds-theme-mode', label: 'Light / Dark Mode' },
      { id: 'ds-themes', label: 'Themes' },
      { id: 'ds-background', label: 'Background Design' },
    ],
  },
  {
    id: 'health-config',
    label: 'Health Configuration',
  },
  {
    id: 'state-management',
    label: 'State Management',
    subSections: [
      { id: 'sm-epic', label: 'Epic States' },
      { id: 'sm-story', label: 'Story States' },
    ],
  },
  {
    id: 'developer-mode',
    label: 'Developer Mode',
  },
];

const devSections: Section[] = [
  {
    id: 'testing-demo',
    label: 'Testing & Demo',
  },
  {
    id: 'epic-story-config',
    label: 'Epic & Story Configuration',
  },
];

const allIds = sections.flatMap((s) => [s.id, ...(s.subSections?.map((sub) => sub.id) ?? [])]);
const devSectionIds = devSections.flatMap((s) => [s.id, ...(s.subSections?.map((sub) => sub.id) ?? [])]);

const SectionHeading: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <Typography id={id} variant="h5" fontWeight={700} gutterBottom sx={{ mt: 5, scrollMarginTop: '80px' }}>
    {children}
  </Typography>
);

const SubHeading: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <Typography id={id} variant="h6" fontWeight={600} gutterBottom sx={{ mt: 3, scrollMarginTop: '80px' }}>
    {children}
  </Typography>
);

const Para: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
    {children}
  </Typography>
);

const Settings: React.FC = () => {
  const dispatch = useAppDispatch();
  const themeMode = useAppSelector((s) => s.theme.mode);
  const selectedThemeId = useAppSelector((s) => s.theme.selectedThemeId);
  const epics = useAppSelector((s) => s.epics.epics);
  const categories = useAppSelector((s) => s.categories.categories);
  const epicWeights = useAppSelector((s) => s.healthConfig.epicWeights);
  const storyWeights = useAppSelector((s) => s.healthConfig.storyWeights);
  const epicStatuses = useAppSelector((s) => s.stateManagement.epicStatuses);
  const storyStatuses = useAppSelector((s) => s.stateManagement.storyStatuses);
  const developerMode = useAppSelector((s) => s.developerMode.developerMode);

  // ── State Management modal ────────────────────────────────────────────────
  const [statusModal, setStatusModal] = useState<{
    open: boolean;
    type: 'epic' | 'story';
    parentState: ParentState;
    editing: StatusEntry | null;
  }>({ open: false, type: 'epic', parentState: 'Open', editing: null });
  const [modalTitle, setModalTitle] = useState('');
  const [modalColor, setModalColor] = useState(categoryColors[0]);
  const [modalError, setModalError] = useState('');

  // ── Import ────────────────────────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState(false);

  // ── Scenario picker dialog ────────────────────────────────────────────────
  const [scenarioDialogOpen, setScenarioDialogOpen] = useState(false);

  // ── Health config local state ─────────────────────────────────────────────
  const [localEpicWeights, setLocalEpicWeights] = useState<HealthWeightMap>(() => ({ ...epicWeights }));
  const [localStoryWeights, setLocalStoryWeights] = useState<HealthWeightMap>(() => ({ ...storyWeights }));
  const [epicWeightsSaved, setEpicWeightsSaved] = useState(false);
  const [storyWeightsSaved, setStoryWeightsSaved] = useState(false);
  const [epicHealthModalOpen, setEpicHealthModalOpen] = useState(false);
  const [storyHealthModalOpen, setStoryHealthModalOpen] = useState(false);

  // Keep local state in sync if Redux state changes externally (e.g. reset)
  useEffect(() => { setLocalEpicWeights({ ...epicWeights }); }, [epicWeights]);
  useEffect(() => { setLocalStoryWeights({ ...storyWeights }); }, [storyWeights]);

  // ── Active section tracking (IntersectionObserver) ────────────────────────
  const [activeId, setActiveId] = useState<string>('display-settings');
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current?.disconnect();
    const callback: IntersectionObserverCallback = (entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible.length > 0) setActiveId(visible[0].target.id);
    };
    observerRef.current = new IntersectionObserver(callback, {
      rootMargin: '-64px 0px -60% 0px',
      threshold: 0,
    });
    // allIds and devSectionIds are module-level constants and never change;
    // only developerMode can vary at runtime.
    const idsToObserve = developerMode ? [...allIds, ...devSectionIds] : allIds;
    idsToObserve.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observerRef.current!.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, [developerMode]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleClearConfig = () => {
    dispatch(setPods([]));
    dispatch(setEpics([]));
    dispatch(setWorkstreams([]));
    const nextMonday = new Date();
    const day = nextMonday.getDay();
    const diff = day === 1 ? 0 : (8 - day) % 7;
    nextMonday.setDate(nextMonday.getDate() + diff);
    nextMonday.setHours(0, 0, 0, 0);
    dispatch(setSprintSettings({
      startDate: nextMonday.toISOString(),
      sprintLength: DEFAULT_SPRINT_LENGTH,
      totalSprints: DEFAULT_TOTAL_SPRINTS,
    }));
    dispatch(setWorkstreamSprints([]));
    try {
      localStorage.removeItem(THEME_MODE_KEY);
      localStorage.removeItem(THEME_ID_KEY);
    } catch {
      // ignore storage errors
    }
  };

  const handleExportEpics = () => {
    const data: EpicExportData = { version: '1.0', epics, categories };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'epic-configuration.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportEpics = (event: React.ChangeEvent<HTMLInputElement>) => {
    setImportError(null);
    setImportSuccess(false);
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data: unknown = JSON.parse(e.target?.result as string);
        if (!validateEpicExportData(data)) {
          setImportError('Invalid schema. Please check your JSON file and try again.');
          return;
        }
        dispatch(setCategories(data.categories));
        dispatch(setEpics(data.epics));
        setImportSuccess(true);
      } catch {
        setImportError('Failed to parse JSON file. Please ensure the file is valid JSON.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleImportClick = () => {
    setImportError(null);
    setImportSuccess(false);
    fileInputRef.current?.click();
  };

  const handleApplyEpicWeights = () => {
    dispatch(setEpicWeights({ ...localEpicWeights }));
    setEpicWeightsSaved(true);
    setTimeout(() => setEpicWeightsSaved(false), 2000);
  };

  const handleApplyStoryWeights = () => {
    dispatch(setStoryWeights({ ...localStoryWeights }));
    setStoryWeightsSaved(true);
    setTimeout(() => setStoryWeightsSaved(false), 2000);
  };

  const handleResetEpicWeights = () => {
    const defaults: HealthWeightMap = Object.fromEntries(
      EPIC_HEALTH_DEFINITIONS.map((d) => [d.id, d.score]),
    );
    setLocalEpicWeights(defaults);
    dispatch(setEpicWeights(defaults));
  };

  const handleResetStoryWeights = () => {
    const defaults: HealthWeightMap = Object.fromEntries(
      STORY_HEALTH_DEFINITIONS.map((d) => [d.id, d.score]),
    );
    setLocalStoryWeights(defaults);
    dispatch(setStoryWeights(defaults));
  };

  // ── Status modal helpers ──────────────────────────────────────────────────
  const openAddStatusModal = (type: 'epic' | 'story', ps: ParentState) => {
    setModalTitle('');
    setModalColor(categoryColors[0]);
    setModalError('');
    setStatusModal({ open: true, type, parentState: ps, editing: null });
  };

  const openEditStatusModal = (type: 'epic' | 'story', ps: ParentState, entry: StatusEntry) => {
    setModalTitle(entry.title);
    setModalColor(entry.color);
    setModalError('');
    setStatusModal({ open: true, type, parentState: ps, editing: entry });
  };

  const handleCloseStatusModal = () => {
    setModalError('');
    setStatusModal((s) => ({ ...s, open: false }));
  };

  const handleSaveStatus = () => {
    if (!modalTitle.trim()) return;
    const { type, parentState, editing } = statusModal;
    const trimmed = modalTitle.trim();
    const allStatuses = type === 'epic' ? epicStatuses : storyStatuses;
    const isDuplicate = PARENT_STATES.some((ps) =>
      allStatuses[ps].some(
        (s) => s.title.toLowerCase() === trimmed.toLowerCase() && (!editing || s.id !== editing.id),
      ),
    );
    if (isDuplicate) {
      setModalError(`A status named "${trimmed}" already exists.`);
      return;
    }
    if (editing) {
      const updated: StatusEntry = { ...editing, title: trimmed, color: modalColor };
      if (type === 'epic') dispatch(updateEpicStatus({ parentState, status: updated }));
      else dispatch(updateStoryStatus({ parentState, status: updated }));
    } else {
      const newEntry = createStatusEntry(trimmed, modalColor);
      if (type === 'epic') dispatch(addEpicStatus({ parentState, status: newEntry }));
      else dispatch(addStoryStatus({ parentState, status: newEntry }));
    }
    handleCloseStatusModal();
  };

  const handleDeleteStatus = () => {
    const { type, parentState, editing } = statusModal;
    if (!editing) return;
    if (type === 'epic') dispatch(removeEpicStatus({ parentState, statusId: editing.id }));
    else dispatch(removeStoryStatus({ parentState, statusId: editing.id }));
    handleCloseStatusModal();
  };

  const handleDragEnd = (type: 'epic' | 'story') => (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    const fromPs = source.droppableId as ParentState;
    const toPs = destination.droppableId as ParentState;
    if (fromPs === toPs) {
      // Reorder within the same column
      const statuses = type === 'epic' ? epicStatuses[fromPs] : storyStatuses[fromPs];
      const reordered = Array.from(statuses);
      const [moved] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, moved);
      if (type === 'epic') dispatch(setEpicStatuses({ parentState: fromPs, statuses: reordered }));
      else dispatch(setStoryStatuses({ parentState: fromPs, statuses: reordered }));
    } else {
      // Move between columns
      if (type === 'epic') {
        dispatch(moveEpicStatus({
          fromParentState: fromPs, toParentState: toPs,
          statusId: draggableId, toIndex: destination.index,
        }));
      } else {
        dispatch(moveStoryStatus({
          fromParentState: fromPs, toParentState: toPs,
          statusId: draggableId, toIndex: destination.index,
        }));
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
      {/* ── Main content ── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>

        {/* ── Display Settings ── */}
        <SectionHeading id="display-settings">Display Settings</SectionHeading>
        <Para>
          Customise how the application looks. Changes take effect immediately and persist for the
          current session.
        </Para>

        <SubHeading id="ds-theme-mode">Light / Dark Mode</SubHeading>
        <Para>Toggle between light and dark colour schemes.</Para>
        <FormControlLabel
          control={
            <Switch
              checked={themeMode === 'dark'}
              onChange={() => dispatch(toggleTheme())}
              color="primary"
            />
          }
          label={themeMode === 'dark' ? 'Dark Mode' : 'Light Mode'}
        />

        <SubHeading id="ds-themes">Themes</SubHeading>
        <Para>Select a colour theme to personalise the application's primary and secondary colours.</Para>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
            gap: 1.5,
            mt: 1,
            maxWidth: 600,
          }}
        >
          {appThemes.map((t) => {
            const colors = themeMode === 'dark' ? t.dark : t.light;
            const isSelected = selectedThemeId === t.id;
            return (
              <Tooltip key={t.id} title={t.name} placement="top">
                <Box
                  onClick={() => dispatch(setSelectedTheme(t.id))}
                  aria-label={`Select ${t.name} theme`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      dispatch(setSelectedTheme(t.id));
                    }
                  }}
                  sx={{
                    position: 'relative',
                    borderRadius: 1,
                    border: isSelected ? '2px solid' : '2px solid transparent',
                    borderColor: isSelected ? 'primary.main' : 'transparent',
                    cursor: 'pointer',
                    p: 1,
                    bgcolor: 'background.paper',
                    boxShadow: isSelected ? 2 : 1,
                    '&:hover': { boxShadow: 3 },
                    outline: 'none',
                  }}
                >
                  {isSelected && (
                    <CheckCircleIcon
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        fontSize: 14,
                        color: 'primary.main',
                      }}
                    />
                  )}
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                    <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: colors.primary }} />
                    <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: colors.secondary }} />
                    <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: colors.backgroundDefault, border: '1px solid', borderColor: 'divider' }} />
                  </Box>
                  <Typography variant="caption" noWrap sx={{ display: 'block', fontSize: '0.75rem' }}>
                    {t.name}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        {/* <SubHeading id="ds-background">Background Design</SubHeading>
        <Para>
          Choose a background design for the application. Each design uses your active theme's
          colours and automatically adapts to light and dark mode.
        </Para>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
            gap: 1.5,
            mt: 1,
            maxWidth: 600,
          }}
        >
          {backgroundDesigns.map((bg) => {
            const isSelected = selectedBackgroundId === bg.id;
            return (
              <Tooltip key={bg.id} title={bg.description} placement="top">
                <Box
                  onClick={() => dispatch(setSelectedBackground(bg.id))}
                  aria-label={`Select ${bg.name} background`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      dispatch(setSelectedBackground(bg.id));
                    }
                  }}
                  sx={{
                    position: 'relative',
                    borderRadius: 1,
                    border: isSelected ? '2px solid' : '2px solid transparent',
                    borderColor: isSelected ? 'primary.main' : 'transparent',
                    cursor: 'pointer',
                    p: 1,
                    bgcolor: 'background.paper',
                    boxShadow: isSelected ? 2 : 1,
                    '&:hover': { boxShadow: 3 },
                    outline: 'none',
                  }}
                >
                  {isSelected && (
                    <CheckCircleIcon
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        fontSize: 14,
                        color: 'primary.main',
                        zIndex: 1,
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      width: '100%',
                      height: 56,
                      borderRadius: 0.5,
                      overflow: 'hidden',
                      mb: 0.5,
                      bgcolor: themeMode === 'dark' ? 'grey.900' : 'grey.100',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {bg.id === 'none' ? (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      </Box>
                    ) : (
                      <BackgroundPreview
                        designId={bg.id}
                        primary={currentThemeColors.primary}
                        secondary={currentThemeColors.secondary}
                        isDark={themeMode === 'dark'}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" noWrap sx={{ display: 'block', fontSize: '0.75rem' }}>
                    {bg.name}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        <Divider sx={{ my: 4 }} /> */}

        <Divider sx={{ my: 4 }} />

        {/* ── Health Configuration ── */}
        <SectionHeading id="health-config">Health Configuration</SectionHeading>
        <Para>
          Adjust the penalty weight for each health category. A higher weight increases the impact
          of that issue on the overall health score. Select a button below to configure the health
          scores for epics or user stories.
        </Para>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AssignmentIcon />}
            onClick={() => setEpicHealthModalOpen(true)}
          >
            Epic Settings
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ArticleIcon />}
            onClick={() => setStoryHealthModalOpen(true)}
          >
            Story Settings
          </Button>
        </Stack>

        {/* ── Epic Health Modal ── */}
        <Dialog
          open={epicHealthModalOpen}
          onClose={() => setEpicHealthModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon sx={{ color: 'primary.main' }} />
              Epic Settings
            </Box>
            <IconButton onClick={() => setEpicHealthModalOpen(false)} size="small" aria-label="Close">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 2 }}>
              These weights apply to health checks performed at the epic level.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {EPIC_HEALTH_DEFINITIONS.map((def) => (
                <Box key={def.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {def.reason}
                  </Typography>
                  <NumberInput
                    value={localEpicWeights[def.id] ?? def.score}
                    onChange={(val) =>
                      setLocalEpicWeights((prev) => ({ ...prev, [def.id]: val }))
                    }
                    min={0}
                    max={100}
                    step={1}
                  />
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleResetEpicWeights}
              sx={{ color: 'text.secondary', borderColor: 'divider' }}
            >
              Reset to Defaults
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => { handleApplyEpicWeights(); setEpicHealthModalOpen(false); }}
            >
              {epicWeightsSaved ? 'Saved!' : 'Apply Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Story Health Modal ── */}
        <Dialog
          open={storyHealthModalOpen}
          onClose={() => setStoryHealthModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ArticleIcon sx={{ color: '#2196F3' }} />
              Story Settings
            </Box>
            <IconButton onClick={() => setStoryHealthModalOpen(false)} size="small" aria-label="Close">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 2 }}>
              These weights apply to health checks performed at the story level.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {STORY_HEALTH_DEFINITIONS.map((def) => (
                <Box key={def.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {def.reason}
                  </Typography>
                  <NumberInput
                    value={localStoryWeights[def.id] ?? def.score}
                    onChange={(val) =>
                      setLocalStoryWeights((prev) => ({ ...prev, [def.id]: val }))
                    }
                    min={0}
                    max={100}
                    step={1}
                  />
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleResetStoryWeights}
              sx={{ color: 'text.secondary', borderColor: 'divider' }}
            >
              Reset to Defaults
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => { handleApplyStoryWeights(); setStoryHealthModalOpen(false); }}
            >
              {storyWeightsSaved ? 'Saved!' : 'Apply Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        <Divider sx={{ my: 4 }} />

        {/* ── State Management ── */}
        <SectionHeading id="state-management">State Management</SectionHeading>
        <Para>
          Configure the child statuses available under each parent state for epics and stories.
          Parent states (<strong>Open</strong>, <strong>In Progress</strong>, <strong>Closed</strong>)
          are fixed and describe the overall lifecycle stage of an item. Statuses are optional labels
          you can assign within each parent state to give more detail (e.g. "Blocked" within "In Progress").
          Drag statuses to reorder them or move them between parent states.
        </Para>

        {/* Epic States */}
        <SubHeading id="sm-epic">Epic States</SubHeading>
        <Para>Drag to reorder or move statuses between columns. Use the edit icon to rename, recolour, or delete a status.</Para>
        <DragDropContext onDragEnd={handleDragEnd('epic')}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
            {PARENT_STATES.map((ps) => (
              <Paper key={ps} variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Chip
                    size="small"
                    label={ps}
                    sx={{ bgcolor: PARENT_STATE_COLORS[ps], color: '#fff', fontWeight: 600, fontSize: '0.72rem' }}
                  />
                </Box>
                <Droppable droppableId={ps}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        minHeight: 60,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                        bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                        borderRadius: 1,
                        transition: 'background-color 0.15s',
                        p: 0.5,
                      }}
                    >
                      {epicStatuses[ps].length === 0 && !snapshot.isDraggingOver && (
                        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', textAlign: 'center', py: 1 }}>
                          No statuses
                        </Typography>
                      )}
                      {epicStatuses[ps].map((st, idx) => (
                        <Draggable key={st.id} draggableId={st.id} index={idx}>
                          {(dragProvided, dragSnapshot) => (
                            <Box
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: dragSnapshot.isDragging ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                px: 0.75,
                                py: 0.5,
                                boxShadow: dragSnapshot.isDragging ? 3 : 0,
                              }}
                            >
                              <Box {...dragProvided.dragHandleProps} sx={{ display: 'flex', alignItems: 'center', color: 'text.disabled', cursor: 'grab' }}>
                                <DragIndicatorIcon sx={{ fontSize: '1rem' }} />
                              </Box>
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: st.color, flexShrink: 0 }} />
                              <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {st.title}
                              </Typography>
                              <Tooltip title="Edit status">
                                <IconButton size="small" onClick={() => openEditStatusModal('epic', ps, st)} sx={{ p: 0.25 }}>
                                  <EditIcon sx={{ fontSize: '0.85rem' }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => openAddStatusModal('epic', ps)}
                  sx={{ mt: 0.5, justifyContent: 'flex-start' }}
                >
                  Add status
                </Button>
              </Paper>
            ))}
          </Box>
        </DragDropContext>

        {/* Story States */}
        <SubHeading id="sm-story">Story States</SubHeading>
        <Para>Drag to reorder or move statuses between columns. Use the edit icon to rename, recolour, or delete a status.</Para>
        <DragDropContext onDragEnd={handleDragEnd('story')}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
            {PARENT_STATES.map((ps) => (
              <Paper key={ps} variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Chip
                    size="small"
                    label={ps}
                    sx={{ bgcolor: PARENT_STATE_COLORS[ps], color: '#fff', fontWeight: 600, fontSize: '0.72rem' }}
                  />
                </Box>
                <Droppable droppableId={ps}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        minHeight: 60,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                        bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                        borderRadius: 1,
                        transition: 'background-color 0.15s',
                        p: 0.5,
                      }}
                    >
                      {storyStatuses[ps].length === 0 && !snapshot.isDraggingOver && (
                        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', textAlign: 'center', py: 1 }}>
                          No statuses
                        </Typography>
                      )}
                      {storyStatuses[ps].map((st, idx) => (
                        <Draggable key={st.id} draggableId={st.id} index={idx}>
                          {(dragProvided, dragSnapshot) => (
                            <Box
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: dragSnapshot.isDragging ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                px: 0.75,
                                py: 0.5,
                                boxShadow: dragSnapshot.isDragging ? 3 : 0,
                              }}
                            >
                              <Box {...dragProvided.dragHandleProps} sx={{ display: 'flex', alignItems: 'center', color: 'text.disabled', cursor: 'grab' }}>
                                <DragIndicatorIcon sx={{ fontSize: '1rem' }} />
                              </Box>
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: st.color, flexShrink: 0 }} />
                              <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {st.title}
                              </Typography>
                              <Tooltip title="Edit status">
                                <IconButton size="small" onClick={() => openEditStatusModal('story', ps, st)} sx={{ p: 0.25 }}>
                                  <EditIcon sx={{ fontSize: '0.85rem' }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => openAddStatusModal('story', ps)}
                  sx={{ mt: 0.5, justifyContent: 'flex-start' }}
                >
                  Add status
                </Button>
              </Paper>
            ))}
          </Box>
        </DragDropContext>

        <Divider sx={{ my: 4 }} />

        {/* ── Developer Mode ── */}
        <SectionHeading id="developer-mode">Developer Mode</SectionHeading>
        <Para>
          Enable Developer Mode to access advanced configuration and debugging features.
          When active, additional sections will be revealed for testing, demo data management,
          and epic &amp; story configuration. A JSON configuration viewer will also be available
          on epic and story forms.
        </Para>
        <FormControlLabel
          control={
            <Switch
              checked={developerMode}
              onChange={() => dispatch(toggleDeveloperMode())}
              color="warning"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CodeIcon sx={{ fontSize: '1.1rem', color: developerMode ? 'warning.main' : 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontWeight: developerMode ? 600 : 400, color: developerMode ? 'warning.main' : 'text.primary' }}>
                {developerMode ? 'Developer Mode Active' : 'Developer Mode Off'}
              </Typography>
            </Box>
          }
        />

        {developerMode && (
          <>
            <Divider sx={{ my: 4 }} />

            {/* ── Testing & Demo ── */}
            <SectionHeading id="testing-demo">Testing &amp; Demo</SectionHeading>
            <Para>
              Load a pre-built demo scenario to explore the application's features, or clear all
              data to start fresh.
            </Para>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 400 }}>
              <Button variant="contained" color="primary" onClick={() => setScenarioDialogOpen(true)}>
                Load Demo Scenario…
              </Button>
              <Button variant="outlined" color="error" onClick={handleClearConfig}>
                Clear Configuration
              </Button>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* ── Epic & Story Configuration ── */}
            <SectionHeading id="epic-story-config">Epic &amp; Story Configuration</SectionHeading>
            <Para>
              Export the current epic and story configuration to a JSON file for backup or sharing.
              Import a previously exported file to restore a configuration.
            </Para>
            {importError && (
              <Alert severity="error" sx={{ mb: 2, maxWidth: 500 }} onClose={() => setImportError(null)}>
                {importError}
              </Alert>
            )}
            {importSuccess && (
              <Alert severity="success" sx={{ mb: 2, maxWidth: 500 }} onClose={() => setImportSuccess(false)}>
                Configuration imported successfully.
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 400 }}>
              <Button variant="contained" color="secondary" onClick={handleExportEpics}>
                Export Epic &amp; Story Details
              </Button>
              <Button variant="outlined" color="primary" onClick={handleImportClick}>
                Import Epic Configuration
              </Button>
              <input
                type="file"
                accept=".json,application/json"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleImportEpics}
              />
            </Box>
          </>
        )}

        <Divider sx={{ my: 4 }} />
        <Typography variant="body2" color="text.secondary" sx={{ pb: 4 }}>
          Settings changes are applied immediately within the current session.
        </Typography>

        {/* ── Scenario Picker Dialog ── */}
        <ScenarioPickerDialog open={scenarioDialogOpen} onClose={() => setScenarioDialogOpen(false)} />

        {/* ── Add / Edit Status Modal ── */}
        <Dialog open={statusModal.open} onClose={handleCloseStatusModal} maxWidth="xs" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {statusModal.editing ? 'Edit Status' : 'Add Status'}
              </Typography>
              <IconButton size="small" onClick={handleCloseStatusModal}><CloseIcon /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Status Name"
                value={modalTitle}
                onChange={(e) => { setModalTitle(e.target.value); setModalError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveStatus()}
                fullWidth
                autoFocus
                error={!!modalError}
                helperText={modalError}
              />
              <Box>
                <Typography variant="body2" gutterBottom>Color</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {categoryColors.map((color) => (
                    <Box
                      key={color}
                      onClick={() => setModalColor(color)}
                      sx={{
                        width: 28, height: 28, borderRadius: '50%', bgcolor: color, cursor: 'pointer',
                        border: '2px solid',
                        borderColor: modalColor === color ? 'text.primary' : 'transparent',
                        '&:hover': { transform: 'scale(1.15)' },
                        transition: 'transform 0.1s',
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: modalColor }} />
                <Typography variant="body2" color="text.secondary">
                  {modalTitle || 'Status preview'}
                </Typography>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <ModalActionFooter
              onSave={handleSaveStatus}
              saveLabel={statusModal.editing ? 'Save' : 'Add'}
              saveDisabled={!modalTitle.trim() || !!modalError}
              onCancel={handleCloseStatusModal}
              onDelete={statusModal.editing ? handleDeleteStatus : undefined}
              deleteLabel="Delete Status"
              deleteConfirmMessage={`Delete status "${modalTitle}"? This cannot be undone.`}
            />
          </DialogActions>
        </Dialog>
      </Box>

      {/* ── Right-side navigation ── */}
      <Box
        component="nav"
        aria-label="Settings sections"
        sx={{
          width: 220,
          flexShrink: 0,
          display: { xs: 'none', md: 'block' },
          position: 'sticky',
          top: 80,
          alignSelf: 'flex-start',
          maxHeight: 'calc(100vh - 100px)',
          overflowY: 'auto',
        }}
      >
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary" fontWeight={600}>
            On this page
          </Typography>
          <List dense disablePadding sx={{ mt: 1 }}>
            {[...sections, ...(developerMode ? devSections : [])].map((section) => (
              <React.Fragment key={section.id}>
                <ListItem disablePadding>
                  <Box
                    component="button"
                    onClick={() => scrollTo(section.id)}
                    sx={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      width: '100%',
                      py: 0.4,
                      px: 0,
                      fontWeight: activeId === section.id ? 700 : 400,
                      color: activeId === section.id ? 'primary.main' : 'text.primary',
                      fontSize: '0.85rem',
                      fontFamily: 'inherit',
                      borderLeft: activeId === section.id ? '3px solid' : '3px solid transparent',
                      borderColor: activeId === section.id ? 'primary.main' : 'transparent',
                      pl: activeId === section.id ? 1 : 0,
                      transition: 'all 0.15s',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    {section.label}
                  </Box>
                </ListItem>
                {section.subSections?.map((sub) => (
                  <ListItem key={sub.id} disablePadding>
                    <Box
                      component="button"
                      onClick={() => scrollTo(sub.id)}
                      sx={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                        width: '100%',
                        py: 0.3,
                        pl: activeId === sub.id ? 2.5 : 1.5,
                        fontWeight: activeId === sub.id ? 600 : 400,
                        color: activeId === sub.id ? 'primary.main' : 'text.secondary',
                        fontSize: '0.78rem',
                        fontFamily: 'inherit',
                        borderLeft: activeId === sub.id ? '3px solid' : '3px solid transparent',
                        borderColor: activeId === sub.id ? 'primary.main' : 'transparent',
                        transition: 'all 0.15s',
                        '&:hover': { color: 'primary.main' },
                      }}
                    >
                      {sub.label}
                    </Box>
                  </ListItem>
                ))}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </Box>
    </Box>
  );
};

export default Settings;
